import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Lightbulb, ArrowRight, ArrowLeft, X } from "lucide-react";

const seenKey = (screenKey, uid) => `zl_coach_${screenKey}_${uid}`;

// Spotlight coach-marks that dim the screen and highlight real buttons the first
// time a screen opens (once per user). Also renders a floating "Tips" button to replay.
export function ScreenTips({ screenKey, steps }) {
  const { user, impersonating } = useAuth();
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [live, setLive] = useState([]);

  const collect = useCallback(
    () => steps.filter((s) => document.querySelector(`[data-testid="${s.target}"]`)),
    [steps]
  );

  useEffect(() => {
    if (!user || impersonating) return;
    if (localStorage.getItem(seenKey(screenKey, user.id))) return;
    // Defer until the role welcome tour has been dismissed so both don't stack.
    if (!localStorage.getItem(`zl_welcome_${user.id}`)) return;
    const t = setTimeout(() => {
      const found = collect();
      if (found.length) { setLive(found); setIdx(0); setRunning(true); }
    }, 750);
    return () => clearTimeout(t);
  }, [user, impersonating, screenKey, collect]);

  const measure = useCallback(() => {
    const step = live[idx];
    if (!step) return;
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    });
  }, [live, idx]);

  useLayoutEffect(() => { if (running) measure(); }, [running, idx, measure]);

  useEffect(() => {
    if (!running) return;
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [running, measure]);

  const finish = () => { if (user) localStorage.setItem(seenKey(screenKey, user.id), "1"); setRunning(false); };
  const replay = () => { const f = collect(); if (f.length) { setLive(f); setIdx(0); setRunning(true); } };
  const next = () => (idx >= live.length - 1 ? finish() : setIdx((v) => v + 1));
  const back = () => setIdx((v) => Math.max(0, v - 1));

  if (!user) return null;

  const step = live[idx];
  const pad = 8;
  let tip = null;
  if (rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const placeBelow = spaceBelow > 210;
    tip = {
      top: placeBelow ? rect.top + rect.height + 14 : Math.max(12, rect.top - 14 - 190),
      left: Math.min(Math.max(12, rect.left), window.innerWidth - 312),
    };
  }

  return (
    <>
      <button
        onClick={replay}
        data-testid={`tips-replay-${screenKey}`}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#7A3FF2] px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:brightness-110 active:translate-y-0.5"
      >
        <Lightbulb size={16} /> Tips
      </button>

      {running && createPortal(
        <AnimatePresence>
          <motion.div className="fixed inset-0 z-[90]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid={`coach-overlay-${screenKey}`}>
            {rect ? (
              <motion.div
                className="pointer-events-none absolute"
                initial={false}
                animate={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                style={{ boxShadow: "0 0 0 9999px rgba(15,23,42,0.62)", border: "3px solid #FF9E1B", borderRadius: 16 }}
              />
            ) : (
              <div className="absolute inset-0 bg-[#0F172A]/60" />
            )}

            {tip && step && (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="absolute w-[300px] rounded-3xl border border-[#E5DECF] bg-white p-5 shadow-2xl"
                style={{ top: tip.top, left: tip.left }}
                data-testid={`coach-step-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#7A3FF2]"><Lightbulb size={13} /> Tip {idx + 1} of {live.length}</span>
                  <button onClick={finish} data-testid="coach-skip" className="rounded-lg p-1 text-[#94A3B8] hover:bg-[#F2ECE1]" aria-label="Skip tips"><X size={16} /></button>
                </div>
                <h3 className="mt-2 font-heading text-lg font-extrabold text-[#1F2937]">{step.title}</h3>
                <p className="mt-1 font-body text-sm text-[#6B7280]">{step.body}</p>
                <div className="mt-4 flex items-center justify-between">
                  <button onClick={back} disabled={idx === 0} data-testid="coach-back" className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold text-[#6B7280] hover:bg-[#F2ECE1] disabled:opacity-40">
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button onClick={next} data-testid="coach-next" className="inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-5 py-2 text-sm font-bold text-white hover:brightness-110">
                    {idx >= live.length - 1 ? "Got it" : <>Next <ArrowRight size={15} /></>}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
